/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import handler from '../../../../share/util/apiResultValidator';
import session from '../../../../share/session';
import customTable from '../../../../share/components/vue/PaginationTable';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import utils from "../../../../share/util/utils";
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import ApiService from "../../../../share/util/api/service";

/** 各フェーズ定義 */
const PHASE = {
  START: "start",
  CHECK: "check",
  SUCCESS: "success",
  CANCEL: "cancel",
  UNREGISTERED: "unregistered",
  FAILED: "failed",
  ERROR: "error"
};

export default {
  name: "requirement131",
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      requirementId: 131,
      tvvData: [],
      tvvTableItems: [],
      requestId: '',
      saveDate: '',
      saveDataId: '',
      modal_requirement_131: false
    };
  },

  components: {
    'custom-table': customTable
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      userName: state => state.userProperty.userName,
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    tvvTableReadColumns() {
      return [
        { name: 'tvv', label: 'individual.header_table_tvv' },
        { name: 'value', label: 'individual.header_table_read_value' },
      ];
    },

    tvvTableSaveColumns() {
      return [
        { name: 'tvv', label: 'individual.header_table_tvv' },
        { name: 'value', label: 'individual.header_table_save_value' },
      ];
    },

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.UNREGISTERED:
        case PHASE.FAILED:
        case PHASE.ERROR:
          return "danger";
        case PHASE.SUCCESS:
          return "success";
        case PHASE.START:
        case PHASE.CHECK:
        case PHASE.CANCEL:
        default:
          return "secondary";
      }
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.modal_requirement_131 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // リソース(作業サポート_DID)のi18nキー取得
      this.i18nWsDidKey = `wsDid${this.systemId}`;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_131 = false;
    },

    /** 中止表示画面の表示 */
    cancelTransition() {
      this.phase = PHASE.CANCEL;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      this.isLoading = false;
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result handler.validate で返ってきたバリデーション結果
    */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** tvvデータを表示用配列に格納 */
    createTvvTableItems() {
      this.tvvTableItems = [];
      this.tvvData.forEach(item => {
        this.tvvTableItems.push({
          tvv: this.$t(`${this.i18nWsDidKey}.${item.did}`),
          value: item.data_record,
        });
      });
    },

    /** TVVロックフラグ読出し処理 */
    async readTvvLockFlg() {
      try {
        this.isLoading = true;

        // API実行
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read-lock'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              switch (response.data.user_data.result) {
                case 'ok':
                  await this.readTvvData();
                  break;
                case 'ng':
                  this.phase = PHASE.UNREGISTERED;
                  break;
              }
              break;
            case REQUEST_STATUS_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** TVV読出し処理 */
    async readTvvData() {
      try {
        this.isLoading = true;

        // API実行
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read-tvv'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // TVV保存画面へ遷移させる
              this.phase = PHASE.CHECK;
              // 表生成
              this.tvvData = response.data.user_data.readout_data;
              this.$nextTick(() => {
                this.createTvvTableItems();
              });
              // データ保存用リクエストID
              this.requestId = response.data.user_data.request_id;
              break;
            case REQUEST_STATUS_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** Tvvデータ保存処理 */
    async saveTvvData() {
      try {
        this.isLoading = true;
        //バックエンドAPI POST
        const endPoint = '/readData/requirements/' + this.requirementId + '/netApps/' + this.netAppId;
        const reqBody = { request_id: this.requestId, user_name: this.userName };
        const response = await ApiService.postCall(ApiService.WSP, endPoint, reqBody);
        // APIレスポンスの検証
        handler.validate(handler.validateTypes.all, response, this, null, () => {
          // 処理が成功したときのコールバック処理
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // Tvv保存完了画面へ遷移
              this.phase = PHASE.SUCCESS;
              // 保存画面で表示する文言を設定
              this.saveDate = utils.timeConverter(response.data.data_id); // data_id(UNIXTIME)をYYYY/MM/DD HH:mm:ss形式に変換
              this.saveDataId = response.data.data_id;
              this.$nextTick(() => {
                this.createTvvTableItems();
              });
              break;
            case REQUEST_STATUS_FAILED:
            default:
              // Tvv異常終了画面へ遷移
              this.phase = PHASE.FAILED;
              break;
          }
        }, (result) => {
          // 処理が失敗したときのコールバック処理
          // セッションが不正・ネットワークエラーならログアウト処理を行う
          if (result.causeType === handler.causeTypes.networkError ||
            result.causeType === handler.causeTypes.invalidSessionToken) {
            if (result.behaviorType === handler.behaviorTypes.logout) {
              this.logout(this);
            } else if (result.behaviorType === handler.behaviorTypes.resetSession) {
              this.resetSession(this);
            }
          } else if (result.causeType === handler.causeTypes.permissionError) {
            // ユーザー権限エラーの場合、ログアウトする
            this.logout(this);
          } else {
            // Tvv異常終了画面へ遷移
            this.phase = PHASE.FAILED;
          }
        });
      } finally {
        this.isLoading = false;
      }
    },
  },

  async mounted() {
    this.init();
  }
};